import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Loader2, LogOut, Sparkles, Wand2 } from 'lucide-react';
import {
  FileDropZone,
  Modal,
  RateLimitInline,
  RESUME_ACCEPT_ATTR,
  validateResumeFile,
} from '@qh/shared';
import { ApiError, RateLimitError, candidateApi, type MasterDataRef } from '../../lib/apiClient';
import { useAuth } from '../../context/AuthContext';
import {
  ProfileFormSections,
  profileFieldId,
  validateProfileForm,
} from './ProfileFormSections';
import {
  buildUpdateBody,
  emptyForm,
  formFromParsed,
  formFromProfile,
  type ProfileFormState,
} from './profileFormShape';
import type { ApplyContext } from './ProfileCompletionGate';

/**
 * Phase 7-dashboard hotfix round 3 — dismissible profile-completion modal.
 *
 * Two-variant modal:
 *   - `variant="soft"`  — opened from the banner. Buttons: [Sign Out] [Save Profile].
 *   - `variant="hard"`  — opened from the Apply CTA. Same buttons; closing cancels
 *     the apply and surfaces a toast ("Complete your profile to apply for jobs").
 *
 * Two internal steps in both variants:
 *   - Step 1: Resume upload + Parse / Manual entry
 *   - Step 2: Review + edit profile fields → PATCH `/candidates/me/profile`
 *
 * Both variants are dismissible: the X icon, ESC, and backdrop click all close the
 * modal without saving. Apply-time enforcement happens at the job level — there's
 * no Skip path that pretends the profile is complete.
 */

interface Props {
  open: boolean;
  variant: 'soft' | 'hard';
  applyContext?: ApplyContext | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

export function ProfileCompletionModal({
  open,
  variant,
  applyContext,
  onClose,
  onSaved,
}: Props) {
  const { candidate, signOut } = useAuth();
  const [step, setStep] = useState<'upload' | 'review'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const [parsing, setParsing] = useState(false);
  const [manualLoading, setManualLoading] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parseRateLimitedUntil, setParseRateLimitedUntil] = useState<number | null>(null);
  const [parsedVia, setParsedVia] = useState<'parsed' | 'manual'>('manual');
  const [unmatchedSkillNames, setUnmatchedSkillNames] = useState<string[]>([]);

  const [form, setForm] = useState<ProfileFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /**
   * Skill rows hydrated from `/public/skills?ids=...` after a successful parse,
   * so SkillPickerField can resolve names for matched_skill_ids that fall
   * outside its bootstrap top-50 catalog. Modal is always entered fresh, so we
   * only need the parse-path seeding here (no profile-load case like MyProfilePage).
   */
  const [skillCatalog, setSkillCatalog] = useState<MasterDataRef[]>([]);
  const mergeIntoSkillCatalog = useCallback((rows: MasterDataRef[]) => {
    if (rows.length === 0) return;
    setSkillCatalog((prev) => {
      const seen = new Set(prev.map((s) => s.id));
      const additions = rows.filter((s) => !seen.has(s.id));
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, []);

  // Reset internal state when the modal closes.
  useEffect(() => {
    if (open) return;
    const t = window.setTimeout(() => {
      setStep('upload');
      setFile(null);
      setFileError(null);
      setParsing(false);
      setManualLoading(false);
      setParseError(null);
      setParseRateLimitedUntil(null);
      setParsedVia('manual');
      setUnmatchedSkillNames([]);
      setForm(emptyForm());
      setSaving(false);
      setSaveError(null);
      setSkillCatalog([]);
    }, 200);
    return () => window.clearTimeout(t);
  }, [open]);

  const handleParse = useCallback(async () => {
    if (!file) return;
    setParsing(true);
    setParseError(null);
    setParseRateLimitedUntil(null);
    try {
      const result = await candidateApi.profile.parseUpload(file);
      setForm(formFromParsed(result.parsed, result.matched_skill_ids));
      setUnmatchedSkillNames(result.unmatched_skill_names);
      setParsedVia('parsed');
      setStep('review');
      // Hydrate names for matched skill ids so chips render names rather than UUIDs.
      // Best-effort: failure leaves the picker to fall back to its existing
      // search-driven hydration if the user types into it.
      if (result.matched_skill_ids.length > 0) {
        try {
          const hydrated = await candidateApi.publicSkillsByIds(result.matched_skill_ids);
          mergeIntoSkillCatalog(hydrated);
        } catch {
          /* swallow — chips fall back to UUID until user searches */
        }
      }
    } catch (err) {
      if (err instanceof RateLimitError) {
        setParseRateLimitedUntil(Date.now() + err.retryAfterSeconds * 1000);
      } else if (err instanceof ApiError) {
        // AI provider failure — backend still persisted the resume. Per analysis §4.3,
        // flip into Step 2 with an empty form so the candidate can finish manually.
        if (err.code === 'AI_SERVICE_UNAVAILABLE' || err.code === 'AI_PROVIDER_FAILED') {
          setParseError('AI parsing is unavailable right now — let\'s enter your details manually. Your resume was saved.');
          setForm(emptyForm());
          setParsedVia('manual');
          setStep('review');
        } else {
          // Append manual-entry guidance to the backend's "couldn't extract text
          // from this PDF" message (backend/src/common/resume-extract/extract-resume-text.ts)
          // so the user has a clear next step. Scoped tightly via prefix match
          // so other backend ApiError messages render unchanged. Button label
          // matches the verbatim JSX below ("Enter manually", lowercase 'm').
          const PDF_EXTRACT_FAILURE_PREFIX = "We couldn't extract text from this PDF.";
          if (err.message.startsWith(PDF_EXTRACT_FAILURE_PREFIX)) {
            setParseError(
              err.message +
                ' Alternatively, you can complete your profile manually by clicking "Enter manually" below.',
            );
          } else {
            setParseError(err.message);
          }
        }
      } else {
        setParseError('Could not parse the resume. Please try again.');
      }
    } finally {
      setParsing(false);
    }
  }, [file, mergeIntoSkillCatalog]);

  const handleManual = useCallback(async () => {
    setParsedVia('manual');
    setUnmatchedSkillNames([]);
    // Fetch the saved profile and hydrate the FULL form via formFromProfile —
    // mirrors how MyProfilePage loads + maps the profile (candidateApi.profile.get
    // → setForm(formFromProfile(p)) → mergeIntoSkillCatalog). A returning
    // candidate sees every field they've already saved pre-filled (no more
    // re-typing on each re-open); a brand-new candidate sees an empty form
    // (formFromProfile maps the mostly-null /profile response to '' / false / []).
    // Skill chips render as names (not UUIDs) because mergeIntoSkillCatalog
    // hydrates the picker the same way the parse-success path does.
    //
    // Error fallback (transient network): degrade to today's name-seeded form
    // so the manual path is never blocked.
    //
    // Known follow-up, NOT fixed here: the parse path (`handleParse` success
    // and AI-failure fallback) has the same "overwrite saved data" issue and
    // needs a product decision on merge vs. overwrite semantics — separate
    // commit.
    if (!candidate) {
      setForm(emptyForm());
      setStep('review');
      return;
    }
    setManualLoading(true);
    try {
      const p = await candidateApi.profile.get();
      setForm(formFromProfile(p));
      mergeIntoSkillCatalog(p.skills ?? []);
    } catch {
      // Transient-failure safety: today's behavior. No toast — the form is
      // still usable, the user just sees a near-empty form as before.
      setForm({ ...emptyForm(), full_name: candidate.full_name ?? '' });
    } finally {
      setManualLoading(false);
      setStep('review');
    }
  }, [candidate, mergeIntoSkillCatalog]);

  const handleSave = useCallback(async () => {
    const invalid = validateProfileForm(form);
    if (invalid) {
      setSaveError(invalid.message);
      const el = document.getElementById(profileFieldId(invalid.field));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.querySelector<HTMLInputElement>('input')?.focus({ preventScroll: true });
      }
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const body = buildUpdateBody(form, {
        completion: { gate: variant, via: parsedVia },
        forceSnapshot: true,
      });
      await candidateApi.profile.update(body);
      await onSaved();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Could not save your profile. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [form, variant, parsedVia, onSaved]);

  const dismissUnmatched = useCallback((name: string) => {
    setUnmatchedSkillNames((prev) => prev.filter((s) => s !== name));
  }, []);

  const titleByStep =
    step === 'upload'
      ? variant === 'hard'
        ? 'Complete your profile to apply'
        : 'Welcome — let\'s complete your profile'
      : 'Review your profile';

  const description = useMemo(() => {
    if (step === 'upload') {
      if (variant === 'hard' && applyContext) {
        return `You're applying to ${applyContext.jobTitle}. Companies need a resume + your details before they can review your application.`;
      }
      return 'Step 1 of 2: Upload your resume — we can extract your details with AI, or you can enter them manually.';
    }
    return 'Step 2 of 2: Review what we have and edit anything before saving.';
  }, [step, variant, applyContext]);

  const parseDisabled =
    !file || parsing || (parseRateLimitedUntil !== null && parseRateLimitedUntil > Date.now());

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={true}
      title={titleByStep}
      description={description}
      size="lg"
      footer={
        <ModalFooter
          step={step}
          parsing={parsing}
          parseDisabled={parseDisabled}
          manualLoading={manualLoading}
          saving={saving}
          onSignOut={() => {
            void signOut();
          }}
          onParse={handleParse}
          onManual={handleManual}
          onBack={() => setStep('upload')}
          onSave={handleSave}
        />
      }
    >
      {step === 'upload' ? (
        <div className="space-y-4">
          {variant === 'soft' && candidate && (
            <p className="rounded-2xl bg-brand-secondary/40 px-4 py-3 text-sm text-brand-deep">
              Region: <span className="font-bold">{candidate.region.name}</span> (locked at signup)
            </p>
          )}
          <FileDropZone
            file={file}
            onSelect={(f) => {
              setFile(f);
              setFileError(null);
            }}
            validate={validateResumeFile}
            accept={RESUME_ACCEPT_ATTR}
            label="Drop your resume here, or click to browse"
            hint="PDF or DOCX, max 5 MB"
            disabled={parsing}
            externalError={fileError}
          />

          <RateLimitInline deadlineMs={parseRateLimitedUntil} />
          {parseError && <p className="text-sm text-red-700 font-medium">{parseError}</p>}
          {parsing && (
            <div className="flex items-center gap-2 text-sm text-brand-muted">
              <Loader2 className="w-4 h-4 animate-spin text-brand-accent" />
              Parsing your resume — this can take 5–10 seconds.
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {parsedVia === 'parsed' && (
            <p className="inline-flex items-center gap-2 rounded-full bg-brand-accent/10 border border-brand-accent/20 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-brand-deep">
              <Sparkles className="w-3 h-3 fill-brand-accent text-brand-accent" />
              Pre-filled from your resume
            </p>
          )}
          {candidate && (
            <ProfileFormSections
              form={form}
              setForm={setForm}
              email={candidate.email}
              regionName={candidate.region.name}
              initialSkillCatalog={skillCatalog}
              unmatchedSkillNames={unmatchedSkillNames}
              onDismissUnmatchedSkill={dismissUnmatched}
            />
          )}
          {saveError && <p className="text-sm text-red-700 font-medium">{saveError}</p>}
        </div>
      )}
    </Modal>
  );
}

interface FooterProps {
  step: 'upload' | 'review';
  parsing: boolean;
  parseDisabled: boolean;
  manualLoading: boolean;
  saving: boolean;
  onSignOut: () => void;
  onParse: () => void;
  onManual: () => void;
  onBack: () => void;
  onSave: () => void;
}

function ModalFooter({
  step,
  parsing,
  parseDisabled,
  manualLoading,
  saving,
  onSignOut,
  onParse,
  onManual,
  onBack,
  onSave,
}: FooterProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 w-full">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSignOut}
          disabled={parsing || saving}
          className="inline-flex items-center gap-1.5 rounded-2xl border border-gray-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-brand-muted hover:text-brand-primary hover:border-brand-primary disabled:opacity-50 transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" />
          Sign out
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {step === 'upload' ? (
          <>
            <button
              type="button"
              onClick={onManual}
              disabled={parsing || manualLoading}
              className="inline-flex items-center gap-1.5 rounded-2xl border border-gray-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-brand-primary hover:border-brand-primary disabled:opacity-50 transition-colors"
            >
              {manualLoading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Loading…
                </>
              ) : (
                <>
                  Enter manually
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onParse}
              disabled={parseDisabled}
              className="inline-flex items-center gap-1.5 rounded-2xl bg-brand-primary px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-white hover:bg-brand-deep disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
            >
              {parsing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Parsing…
                </>
              ) : (
                <>
                  <Wand2 className="w-3.5 h-3.5" />
                  Parse with AI
                </>
              )}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onBack}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-2xl border border-gray-200 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-brand-primary hover:border-brand-primary disabled:opacity-50 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-2xl bg-brand-primary px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-white hover:bg-brand-deep disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  Save profile
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
